/**
 * Admin data service — the single seam between the FE and the §6.1 contract.
 * Mirrors the payment app's `pricingService` shape (takes a client, returns
 * typed data), but adds a mock switch so the FE can run without the backend.
 *
 * Toggle with `REACT_APP_USE_MOCK`:
 *   - "true"  → local mock (`mockAdminData.ts`), no backend needed.
 *   - unset/other → real GraphQL against `REACT_APP_GRAPHQL_ENDPOINT`.
 *
 * Consume via `useAdminService()`; the returned object is stable per client.
 */
import { useMemo } from "react";
import type { GraphQLClient } from "graphql-request";
import { useGraphQLClient } from "../graphql/client";
import {
  ADMIN_DASHBOARD_KPIS,
  ADMIN_DASHBOARD_SERIES,
  ADMIN_IMPORT_ORIGINS,
  ADMIN_FEATURE_USAGE,
  ADMIN_USERS,
} from "../graphql/adminQueries";
import type {
  AdminDashboardKpis,
  AdminDashboardSeries,
  AdminImportOrigins,
  AdminFeatureUsage,
  AdminRangeVars,
  AdminUserConnection,
  AdminUsersVars,
} from "../graphql/types";
import {
  mockDashboardKpis,
  mockDashboardSeries,
  mockImportOrigins,
  mockFeatureUsage,
  mockUsers,
} from "./mockAdminData";

export const USE_MOCK = process.env.REACT_APP_USE_MOCK === "true";

export interface AdminService {
  getDashboardKpis(vars: AdminRangeVars): Promise<AdminDashboardKpis>;
  getDashboardSeries(vars: AdminRangeVars): Promise<AdminDashboardSeries>;
  getImportOrigins(vars: AdminRangeVars): Promise<AdminImportOrigins>;
  getFeatureUsage(vars: AdminRangeVars): Promise<AdminFeatureUsage>;
  getUsers(vars: AdminUsersVars): Promise<AdminUserConnection>;
}

/** Small artificial latency so loading/skeleton states are exercised in mock mode. */
function delayed<T>(value: T, ms = 350): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

function createMockService(): AdminService {
  return {
    getDashboardKpis: (vars) => delayed(mockDashboardKpis(vars)),
    getDashboardSeries: (vars) => delayed(mockDashboardSeries(vars)),
    getImportOrigins: (vars) => delayed(mockImportOrigins(vars)),
    getFeatureUsage: (vars) => delayed(mockFeatureUsage(vars)),
    getUsers: (vars) => delayed(mockUsers(vars)),
  };
}

function createGraphQLService(client: GraphQLClient): AdminService {
  return {
    async getDashboardKpis(vars) {
      // KPIs need only the concrete window bounds — `range` is not part of this query.
      const data = await client.request<{ adminDashboardKpis: AdminDashboardKpis }>(
        ADMIN_DASHBOARD_KPIS,
        { from: vars.from, to: vars.to },
      );
      return data.adminDashboardKpis;
    },
    async getDashboardSeries(vars) {
      const data = await client.request<{ adminDashboardSeries: AdminDashboardSeries }>(
        ADMIN_DASHBOARD_SERIES,
        vars,
      );
      return data.adminDashboardSeries;
    },
    async getImportOrigins(vars) {
      // Like the KPIs, this aggregates over the window — no bucket granularity.
      const data = await client.request<{ adminImportOrigins: AdminImportOrigins }>(
        ADMIN_IMPORT_ORIGINS,
        { from: vars.from, to: vars.to },
      );
      return data.adminImportOrigins;
    },
    async getFeatureUsage(vars) {
      const data = await client.request<{ adminFeatureUsage: AdminFeatureUsage }>(
        ADMIN_FEATURE_USAGE,
        vars,
      );
      return data.adminFeatureUsage;
    },
    async getUsers(vars) {
      const data = await client.request<{ adminUsers: AdminUserConnection }>(
        ADMIN_USERS,
        vars,
      );
      return data.adminUsers;
    },
  };
}

export function useAdminService(): AdminService {
  const client = useGraphQLClient();
  return useMemo(
    () => (USE_MOCK ? createMockService() : createGraphQLService(client)),
    [client],
  );
}
